// Libs
import { type FormEvent, useMemo, useState } from "react";

// Utils
import { useTranslation } from "src/design-system/core/TranslationContext";

// Components
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";
import { isEmpty, isNil, max, orderBy } from "lodash";
import { USER_TABLE_PAGING_OPTIONS } from "src/constants";
import type { Item } from "src/types/item";
import type { Pos } from "src/types/pos";
import { ItemDisplay } from "src/views/marketing-actions/components/Items/ItemBox";
import { OFFER_TRANSLATION_NAMESPACE } from "src/views/offer/config";

export type ActionSpecial = "suspend" | "reactivate";
interface OfferCharacteristicProductModalProps {
    onConfirm: (e?: FormEvent<HTMLFormElement>) => void;
    isOpen: boolean;
    onClose: (boolean) => void;
    itemListByPos?: Record<number, Item[]>;
    numberOfProductsByPos?: Record<number, any>;
    posList?: Pos[];
    numberOfItems?: number;
    isCategorySelection: boolean;
}

export const OFFER_SELECT_PRODUCT_TK = "OFFER_SELECT_PRODUCT_TK";

export function OfferCharacteristicProductModal({
    onConfirm,
    isOpen: isOpenDialog,
    onClose,
    itemListByPos = {},
    numberOfProductsByPos = {},
    numberOfItems = 0,
    posList,
    isCategorySelection,
}: OfferCharacteristicProductModalProps): JSX.Element {
    const { t, translateCommon } = useTranslation(
        `${OFFER_TRANSLATION_NAMESPACE}:${OFFER_SELECT_PRODUCT_TK}`
    );

    const handleClose = () => {
        onClose(false);
    };

    const handleConfirm = async () => {
        onClose(false);
        onConfirm();
    };
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const visiblePosList = useMemo(
        () =>
            orderBy(posList, ["name"]).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [page, rowsPerPage, posList.map((elm) => elm.name).join(",")]
    );

    if (isNil(visiblePosList) || visiblePosList.length === 0 || isEmpty(itemListByPos)) {
        return null;
    }

    return (
        <Dialog open={isOpenDialog} maxWidth="xl" onClose={handleClose}>
            <DialogTitle id="alert-dialog-title">
                <Typography variant="h3">
                    {isCategorySelection ? t("title-category") : t("title")}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DialogContent>
                    <DialogContentText>
                        {/* NEW TABLE */}
                        <TableContainer>
                            <Table stickyHeader size="small" aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ whiteSpace: "nowrap" }}>
                                            {t("columns.pos")}
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: "nowrap" }}>
                                            {isCategorySelection
                                                ? t("columns.nbCategories")
                                                : t("columns.nbProducts")}
                                        </TableCell>
                                        <TableCell>
                                            {isCategorySelection
                                                ? t("columns.categories")
                                                : t("columns.products")}
                                        </TableCell>
                                        {Array(
                                            max(
                                                Object.values(numberOfProductsByPos).map(
                                                    (e) => e.numberOfProduct
                                                )
                                            )
                                        )
                                            .fill(1)
                                            .map((_elm, index) => {
                                                return <TableCell key={index}>{""}</TableCell>;
                                            })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(visiblePosList ?? []).map((pos) => {
                                        const row = itemListByPos[pos.id] ?? [];
                                        const currentNumberOfProduct =
                                            numberOfProductsByPos[pos.id]?.numberOfProduct ?? 0;

                                        return (
                                            <TableRow key={pos.id} style={{ height: "60px" }}>
                                                <TableCell>{pos.name}</TableCell>
                                                <TableCell>
                                                    {currentNumberOfProduct} / {numberOfItems}
                                                </TableCell>
                                                {((row as any[]) ?? []).map((elm) => (
                                                    <TableCell key={elm.name}>
                                                        <ItemDisplay
                                                            itemName={elm.name}
                                                            categoryName={
                                                                isCategorySelection
                                                                    ? ""
                                                                    : (elm.remoteCategory?.name ??
                                                                      t("category.general"))
                                                            }
                                                        />
                                                    </TableCell>
                                                ))}
                                                {Array(
                                                    (max(
                                                        Object.values(numberOfProductsByPos).map(
                                                            (e) => e.numberOfProduct
                                                        )
                                                    ) ?? 0) - ((row as any) ?? [])?.length
                                                )
                                                    .fill(1)
                                                    .map((_elm, index) => {
                                                        return (
                                                            <TableCell key={index}>{""}</TableCell>
                                                        );
                                                    })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={USER_TABLE_PAGING_OPTIONS}
                                component="div"
                                count={posList?.length ?? 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableContainer>
                    </DialogContentText>
                </DialogContent>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleConfirm} color="primary">
                    {translateCommon("actions.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
